import { STATUS } from "./statuses";
import { getProductBySku } from "./products";

/**
 * Product Family / Range — catalogue presentation layer over Product Master.
 * Products page and Shop browse ranges; variants live in products.js.
 */

export const PRODUCT_RANGES = [
  {
    id: "led-bulbs",
    slug: "led-bulbs",
    name: "DEBSUN LED Bulbs",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "Efficient LED bulbs for residential, commercial and light industrial applications.",
    description:
      "DEBSUN LED bulb range engineered for efficiency and everyday reliability. Available in multiple wattages with daylight colour temperature and wide-voltage operation.",
    masterImage: "/products/lighting/led-bulbs/master/range.png",
    rangeLabel: "7W – 15W",
    variants: [
      "DB-LED-BLB-07",
      "DB-LED-BLB-09",
      "DB-LED-BLB-12",
      "DB-LED-BLB-15",
    ],
    applications: [
      "Homes and apartments",
      "Offices and retail",
      "Utility areas",
      "General indoor lighting",
    ],
    features: [
      "Up to 85% energy saving (catalogue claim)",
      "Long life",
      "Eye comfort design intent",
      "Wide voltage 100V – 300V AC",
      "B22 base",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Bulb", "Lighting"],
    seo: {
      title: "DEBSUN LED Bulbs 7W–15W | Product Range",
      description:
        "DEBSUN LED bulb range — 7W, 9W, 12W and 15W daylight variants for efficient indoor lighting. Request a quote.",
    },
  },
  {
    id: "led-bulbs-hp",
    slug: "led-bulbs-hp",
    name: "DEBSUN High-Power LED Bulbs",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "Higher-wattage LED bulbs for larger spaces and demanding duty.",
    description:
      "High-power DEBSUN LED bulb range for spaces that need stronger illumination. Daylight colour temperature with wide-voltage operation and B22 base.",
    masterImage: "/products/lighting/led-bulbs-hp/master/range.png",
    rangeLabel: "20W – 50W",
    variants: [
      "DB-LED-BLB-20",
      "DB-LED-BLB-30",
      "DB-LED-BLB-40",
      "DB-LED-BLB-50",
    ],
    applications: [
      "Large rooms and halls",
      "Workshops",
      "Commercial interiors",
      "High-ceiling indoor areas",
    ],
    features: [
      "Up to 85% energy saving (catalogue claim)",
      "Long life",
      "Eye comfort design intent",
      "Wide voltage 100V – 300V AC",
      "B22 base",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Bulb", "High Power", "Lighting"],
    seo: {
      title: "DEBSUN High-Power LED Bulbs 20W–50W | Product Range",
      description:
        "DEBSUN high-power LED bulbs — 20W, 30W, 40W and 50W daylight variants. Request a quote from DEBSUN Technologies.",
    },
  },
  {
    id: "panel-lights",
    slug: "panel-lights",
    name: "DEBSUN Round LED Panel Lights",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "Round LED panel lights for clean, even indoor illumination.",
    description:
      "DEBSUN round LED panel light range for modern interiors. Available in multiple wattages and sizes with daylight colour temperature and wide-voltage operation. DOB and driver technology options available — confirm configuration with engineering.",
    masterImage: "/products/lighting/panel-lights/master/range.png",
    rangeLabel: "8W – 22W",
    variants: [
      "DB-LED-PL-08",
      "DB-LED-PL-12",
      "DB-LED-PL-15",
      "DB-LED-PL-22",
    ],
    applications: [
      "Offices and workspaces",
      "Homes and corridors",
      "Retail interiors",
      "Commercial ceilings",
    ],
    features: [
      "Up to 85% energy saving (catalogue claim)",
      "Long life",
      "Eye comfort design intent",
      "Wide voltage 100V – 300V AC",
      "Round form factor",
      "DOB / driver technology options on request",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Panel", "Round", "Lighting"],
    seo: {
      title: "DEBSUN Round LED Panel Lights 8W–22W | Product Range",
      description:
        "DEBSUN round LED panel lights — 8W, 12W, 15W and 22W daylight variants. Request a quote.",
    },
  },
  {
    id: "flood-lights",
    slug: "flood-lights",
    name: "DEBSUN LED Flood Lights",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "Industrial LED flood lights with Gamma and Lens optic options.",
    description:
      "DEBSUN LED flood light range for yards, loading docks and area lighting. Choose Gamma (wide beam) or Lens (focused beam) optics. Daylight colour temperature with all-weather construction intent.",
    masterImage: "/products/lighting/flood-lights/master/range.png",
    rangeLabel: "50W – 200W",
    variants: ["DB-LED-FL-050", "DB-LED-FL-100", "DB-LED-FL-200"],
    applications: [
      "Loading docks and yards",
      "Outdoor storage areas",
      "Industrial approach roads",
      "Perimeter and facade wash",
    ],
    features: [
      "Up to 85% energy saving (catalogue claim)",
      "Long life",
      "High brightness design intent",
      "Gamma / Lens optic options",
      "All-weather construction intent",
      "220–240V AC",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Floodlight", "Outdoor", "Gamma", "Lens"],
    seo: {
      title: "DEBSUN LED Flood Lights 50W–200W | Product Range",
      description:
        "DEBSUN LED flood lights — 50W, 100W and 200W with Gamma or Lens optics. Request a quote.",
    },
  },
  {
    id: "panel-lights-square",
    slug: "panel-lights-square",
    name: "DEBSUN Square LED Panel Lights",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "Square LED panel lights for clean, even indoor illumination.",
    description:
      "DEBSUN square LED panel light range for modern interiors. Available in multiple wattages and sizes with daylight colour temperature and wide-voltage operation. DOB and driver technology options available — confirm configuration with engineering.",
    masterImage: "/products/lighting/panel-lights-square/master/range.png",
    rangeLabel: "8W – 22W",
    variants: [
      "DB-LED-PLS-08",
      "DB-LED-PLS-12",
      "DB-LED-PLS-15",
      "DB-LED-PLS-22",
    ],
    applications: [
      "Offices and workspaces",
      "Homes and corridors",
      "Retail interiors",
      "Commercial ceilings",
    ],
    features: [
      "Up to 85% energy saving (catalogue claim)",
      "Long life",
      "Eye comfort design intent",
      "Wide voltage 100V – 300V AC",
      "Square form factor",
      "DOB / driver technology options on request",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Panel", "Square", "Lighting"],
    seo: {
      title: "DEBSUN Square LED Panel Lights 8W–22W | Product Range",
      description:
        "DEBSUN square LED panel lights — 8W, 12W, 15W and 22W daylight variants. Request a quote.",
    },
  },
  {
    id: "street-lights",
    slug: "street-lights",
    name: "DEBSUN LED Street Lights",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "LED street lights for roads, campuses and outdoor pathways.",
    description:
      "DEBSUN LED street light range engineered for outdoor pathway and roadway illumination. Daylight colour temperature with all-weather construction intent across 24W to 100W.",
    masterImage: "/products/lighting/street-lights/master/range.png",
    rangeLabel: "24W – 100W",
    variants: [
      "DB-LED-SL-024",
      "DB-LED-SL-030",
      "DB-LED-SL-050",
      "DB-LED-SL-100",
    ],
    applications: [
      "Roads and streets",
      "Campus pathways",
      "Industrial approach roads",
      "Parking and perimeter lighting",
    ],
    features: [
      "6500K Daylight",
      "All-weather construction intent",
      "Reliable performance design intent",
      "Built for outdoor continuous duty",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Street Light", "Outdoor", "Lighting"],
    seo: {
      title: "DEBSUN LED Street Lights 24W–100W | Product Range",
      description:
        "DEBSUN LED street lights — 24W, 30W, 50W and 100W daylight variants. Request a quote.",
    },
  },
  {
    id: "tube-lights",
    slug: "tube-lights",
    name: "DEBSUN LED Tube Lights",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "AC and DC LED tube lights for residential, commercial and industrial spaces.",
    description:
      "DEBSUN LED tube light range covering 1-foot and 4-foot formats in AC and DC operation. Suitable for homes, offices, factories and workshops — confirm driver and mounting configuration with engineering.",
    masterImage: "/products/lighting/tube-lights/master/range.png",
    rangeLabel: "5W – 36W",
    variants: [
      "DB-LED-TB-05AC",
      "DB-LED-TB-05DC",
      "DB-LED-TB-20AC",
      "DB-LED-TB-36AC",
    ],
    applications: [
      "Homes, kitchens and offices",
      "Showrooms and cabinets",
      "Factories and warehouses",
      "Workshops and assembly areas",
    ],
    features: [
      "AC and DC operation options",
      "Flicker-free design intent",
      "Easy installation",
      "Residential and industrial application coverage",
      "6500K Daylight (range catalogue)",
    ],
    status: STATUS.AVAILABLE,
    featured: true,
    quoteEnabled: true,
    tags: ["LED", "Tube Light", "T5", "Lighting"],
    seo: {
      title: "DEBSUN LED Tube Lights 5W–36W | Product Range",
      description:
        "DEBSUN LED tube lights — 5W AC/DC and 20W/36W AC 4-foot variants. Request a quote.",
    },
  },
  {
    id: "aurora-highbay",
    slug: "aurora-highbay",
    name: "Aurora Highbay",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    shortDescription:
      "Industrial LED highbay for factories, warehouses and production halls.",
    description:
      "Industrial LED highbay luminaire for factories, warehouses and production halls. Engineered for durable housing and straightforward installation in demanding environments.",
    masterImage: null,
    rangeLabel: "150W",
    variants: ["DB-LED-HB-150"],
    applications: [
      "Warehouses and logistics halls",
      "Manufacturing bays",
      "Steel and metallurgy sheds",
      "Automotive workshops",
    ],
    features: [
      "High-efficiency LED light engine",
      "Industrial-grade housing for continuous duty",
      "Interchangeable optic options",
      "Surge-protected driver stage",
      "Suitable for high mounting heights",
    ],
    status: STATUS.AVAILABLE,
    featured: false,
    quoteEnabled: true,
    tags: ["LED", "Highbay", "Industrial"],
    seo: {
      title: "Aurora Highbay 150W | DEBSUN Product Range",
      description:
        "DEBSUN Aurora Highbay 150W industrial LED luminaire. Request a quote.",
    },
  },
  {
    id: "genesys",
    slug: "genesys",
    name: "DEBSUN GENESYS",
    category: "digital-platforms",
    subcategory: "Industrial Intelligence",
    shortDescription:
      "Long-term industrial intelligence platform initiative.",
    description:
      "Long-term industrial intelligence platform initiative focused on unifying engineering workflows, industrial operations and intelligent decision support.",
    masterImage: null,
    rangeLabel: "Platform",
    variants: ["DB-GENESYS-PLATFORM"],
    applications: [
      "Industrial intelligence",
      "Factory operations visibility",
      "Engineering collaboration",
      "Production analytics",
    ],
    features: [
      "Unified industrial operations direction",
      "Engineering workflow focus",
      "Decision-support ambition",
      "Scalable platform roadmap",
      "Released only after engineering validation",
    ],
    status: STATUS.UNDER_DEVELOPMENT,
    featured: true,
    quoteEnabled: false,
    href: "/genesys",
    tags: ["GENESYS", "Platform"],
    seo: {
      title: "DEBSUN GENESYS | Platform Initiative",
      description:
        "DEBSUN GENESYS industrial intelligence platform — under development.",
    },
  },
  {
    id: "lynda",
    slug: "lynda",
    name: "LYNDA",
    category: "research-initiatives",
    subcategory: "Industrial AI Research",
    shortDescription:
      "Research initiative exploring applied industrial intelligence.",
    description:
      "Research initiative exploring applied industrial intelligence to assist engineering diagnosis, design support and operational decision-making.",
    masterImage: null,
    rangeLabel: "Research",
    variants: ["DB-LYNDA-RESEARCH"],
    applications: [
      "Engineering assistance research",
      "Diagnostics support studies",
      "Decision-support exploration",
      "Industrial data interpretation research",
    ],
    features: [
      "Research preview status",
      "Industrial problem focus",
      "Engineering-first evaluation",
      "No commercial release claim",
      "Aligned with DEBSUN long-term vision",
    ],
    status: STATUS.RESEARCH,
    featured: false,
    quoteEnabled: false,
    tags: ["LYNDA", "Research"],
    seo: {
      title: "LYNDA Research Initiative | DEBSUN",
      description:
        "LYNDA — DEBSUN research into applied industrial intelligence.",
    },
  },
];

export function getAllRanges() {
  return PRODUCT_RANGES;
}

export function getRangeBySlug(slug) {
  if (!slug) return null;
  return (
    PRODUCT_RANGES.find(
      (r) => r.slug.toLowerCase() === String(slug).toLowerCase()
    ) || null
  );
}

export function getRangeById(id) {
  if (!id) return null;
  return PRODUCT_RANGES.find((r) => r.id === id) || null;
}

export function getRangeForProduct(product) {
  if (!product) return null;
  if (product.familyId) return getRangeById(product.familyId);
  return (
    PRODUCT_RANGES.find((r) => (r.variants || []).includes(product.sku)) || null
  );
}

export function getRangeVariants(range) {
  if (!range) return [];
  return (range.variants || []).map(getProductBySku).filter(Boolean);
}

export function getFeaturedRanges() {
  return PRODUCT_RANGES.filter((r) => r.featured);
}

export function getShopRanges() {
  return PRODUCT_RANGES.filter(
    (r) =>
      r.quoteEnabled ||
      r.status === STATUS.UNDER_DEVELOPMENT ||
      r.status === STATUS.RESEARCH ||
      r.status === STATUS.AVAILABLE ||
      r.status === STATUS.CUSTOMISABLE ||
      r.status === STATUS.ENGINEERING_VALIDATION
  );
}

export function getRangesByCategory(categoryId) {
  return PRODUCT_RANGES.filter((r) => r.category === categoryId);
}

export function searchRanges(query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return PRODUCT_RANGES;
  return PRODUCT_RANGES.filter((r) => {
    const variantNames = getRangeVariants(r)
      .map((p) => `${p.sku} ${p.name} ${(p.searchKeywords || []).join(" ")}`)
      .join(" ");
    const haystack = [
      r.name,
      r.slug,
      r.subcategory,
      r.shortDescription,
      r.description,
      r.rangeLabel,
      ...(r.tags || []),
      ...(r.applications || []),
      variantNames,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRangePath(range) {
  if (!range) return "/products";
  if (range.href) return range.href;
  return `/products/${range.slug}`;
}
